import { TableBody, TableHead, TableRow } from '@mui/material';
import Table from '@mui/material/Table';
import TableContainer from '@mui/material/TableContainer';
import {
  Column,
  currencyFormatter,
  ManagedFundsProductUnderlying,
  ProductUnderlyingGroupsList,
  UnitLinkedProductUnderlying,
  walletProductDetailDialogMessages
} from 'azshared';
import React, { Fragment } from 'react';
import { useIntl } from 'react-intl';
import { Loader, LoaderSize } from '../../../../../components/loader/loader';
import { SimpleAccordion } from '../../../../../components/simple-accordion/simple-accordion';
import { AppTableCell } from '../../../../../components/table/table-cell';
import { AppTableCellHeader } from '../../../../../components/table/table-cell-header';
import { mapGroupNameToItalian, sortUnderlyingGroups } from '../../utils/mappers';
import './underlying-table.scss';

interface UnderlyingTableProps {
  underlyingGroups: ProductUnderlyingGroupsList;
  columns: Column<ManagedFundsProductUnderlying>[] | Column<UnitLinkedProductUnderlying>[];
  isLoading: boolean;
}

export const UnderlyingTable = (props: UnderlyingTableProps) => {
  const { underlyingGroups, columns, isLoading } = props;
  const { formatMessage } = useIntl();

  const sortedGroups = sortUnderlyingGroups(underlyingGroups.underlyingGroupsList);

  return (
    <div className='underlying'>
      <div style={{ fontSize: 'inherit' }} className='underlying-total-amount '>
        {formatMessage(walletProductDetailDialogMessages.totalAmount) +
          ' ' +
          currencyFormatter(underlyingGroups?.equivalent?.value ?? 0, { currency: underlyingGroups?.equivalent?.currency })}
      </div>

      {isLoading && (
        <div className='table-loader'>
          <Loader loadersize={LoaderSize.XL} />
        </div>
      )}

      {!isLoading &&
        sortedGroups.map((group) => (
          <SimpleAccordion key={group.name} title={<span className='underlying-chip'> {mapGroupNameToItalian(group.name)} </span>}>
            <TableContainer className='underlying-table' style={{ maxHeight: 400 }}>
              <Table aria-label='table' stickyHeader>
                <TableHead>
                  <TableRow>
                    {columns.map((column, index) => (
                      <AppTableCellHeader key={index} className='underlying-table-header'>
                        {formatMessage(column.headerLabel)}
                      </AppTableCellHeader>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {group.underlying.map((underlying, index) => (
                    <TableRow className='underlying-table-row' key={group.name + index}>
                      {columns.map((cell) => (
                        <Fragment key={cell.key}>
                          {(cell.key === 'percentage' || cell.key === 'marketPriceDate') && (
                            <AppTableCell align='left' type={cell.type}>
                              {(underlying as UnitLinkedProductUnderlying)[cell.key]}
                            </AppTableCell>
                          )}
                          {(cell.key === 'averagePurchaseExchange' ||
                            cell.key === 'rate' ||
                            cell.key === 'marketExchange' ||
                            cell.key === 'incPercentage' ||
                            cell.key === 'riskCoefficientPercentage') && (
                            <AppTableCell align='left' type={cell.type}>
                              {(underlying as ManagedFundsProductUnderlying)[cell.key]}
                            </AppTableCell>
                          )}
                          {(cell.key === 'type' ||
                            cell.key === 'productName' ||
                            cell.key === 'averagePurchasePrice' ||
                            cell.key === 'quantity' ||
                            cell.key === 'marketPrice' ||
                            cell.key === 'equivalent') && (
                            <AppTableCell align='left' type={cell.type}>
                              {group.name === 'BankAccounts' && cell.key === 'quantity'
                                ? underlying[cell.key] === 0
                                  ? '-'
                                  : underlying[cell.key].toFixed(2).replace('.', ',')
                                : underlying[cell.key]}
                            </AppTableCell>
                          )}
                        </Fragment>
                      ))}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </SimpleAccordion>
        ))}
    </div>
  );
};
